/**
 * The global highscore (glunz/v1/highscore, see Glunz\Rest\Highscore).
 *
 * Requests go out without cookies: the endpoints are public, and a logged-in
 * editor's session cookie without a REST nonce would only get in the way.
 */
/**
 * Points come in quarters (a square of the word is worth 0.25): shown without
 * trailing zeros and with a point as the separator — 12, 12.25, 12.5, 12.75.
 */
export function formatScore(score) {
  const quarters = Math.round(Number(score) * 4)
  return Number.isFinite(quarters) ? String(quarters / 4) : '0'
}

const normalize = (data) => ({
  best: typeof data?.best?.score === 'number' ? data.best : null,
  top: Array.isArray(data?.top) ? data.top : [],
})

/** @returns {Promise<{best: {name: string, score: number}|null, top: Array}>} */
export async function fetchBoard(url, signal) {
  const response = await fetch(url, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) throw new Error(`highscore: HTTP ${response.status}`)
  return normalize(await response.json())
}

/**
 * @param {{name: string, score: number, duration: number}} entry
 * @returns {Promise<{status: number, rank: number|null, entry: object|null, best: object|null, top: Array}>}
 */
export async function submitScore(url, entry, signal) {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'omit',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
    signal,
  })
  const data = await response.json().catch(() => null)

  return {
    status: response.status,
    rank: Number.isInteger(data?.rank) ? data.rank : null,
    entry: data?.entry ?? null,
    ...normalize(data),
  }
}

const cell = (className, text) => {
  const span = document.createElement('span')
  span.className = className
  span.textContent = String(text)
  return span
}

function boardItem({ rank, name, score, current = false }) {
  const item = document.createElement('li')
  item.className = 'snake-board-item'
  if (current) {
    item.classList.add('is-current')
    item.setAttribute('aria-current', 'true')
  }
  item.append(
    cell('snake-board-rank', `${rank}.`),
    cell('snake-board-name', name),
    cell('snake-board-score', formatScore(score))
  )
  return item
}

function messageItem(text, className = 'snake-board-message') {
  const item = document.createElement('li')
  item.className = `snake-board-item ${className}`
  item.textContent = text
  return item
}

/** A single line in place of the list: loading, empty or an error. */
export function renderMessage(list, text) {
  list.replaceChildren(messageItem(text))
}

/**
 * The top list; names only ever go in as text. A submitted entry that did not
 * make the top 10 is appended below a gap, highlighted like one that did.
 */
export function renderBoard(list, top, { entry = null, emptyText = '' } = {}) {
  const items = top.map((row) => boardItem(row))

  if (entry && !top.some((row) => row.current)) {
    const gap = messageItem('…', 'snake-board-gap')
    gap.setAttribute('aria-hidden', 'true')
    items.push(gap, boardItem({ ...entry, current: true }))
  }

  if (!items.length && emptyText) items.push(messageItem(emptyText))
  list.replaceChildren(...items)
}
